import React, { useState } from 'react';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { useTranslation } from '../../i18n';
import { tutorService, QuickHelpType } from '../../services/tutorService';

interface QuickHelpButtonsProps {
  lessonId: number;
  onResponse: (response: string, type: QuickHelpType) => void;
  disabled?: boolean;
}

const QuickHelpButtons: React.FC<QuickHelpButtonsProps> = ({
  lessonId,
  onResponse,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<QuickHelpType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const quickHelpOptions = [
    { type: 'explain' as QuickHelpType, label: t('tutor.quickHelp.explain', 'Einfacher'), icon: '💡' },
    { type: 'example' as QuickHelpType, label: t('tutor.quickHelp.example', 'Beispiel'), icon: '📝' },
    { type: 'quiz' as QuickHelpType, label: t('tutor.quickHelp.quiz', 'Teste mich'), icon: '❓' },
    { type: 'summary' as QuickHelpType, label: t('tutor.quickHelp.summary', 'Zusammenfassung'), icon: '📋' },
  ];

  const handleQuickHelp = async (type: QuickHelpType) => {
    setLoading(type);
    setError(null);

    try {
      const response = await tutorService.getQuickHelp(lessonId, type);
      onResponse(response, type);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler bei der Anfrage');
      console.error('Quick help error:', err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {quickHelpOptions.map((option) => (
        <Tooltip key={option.type} title={t(`tutor.quickHelp.${option.type}Tooltip`, option.label)}>
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleQuickHelp(option.type)}
              disabled={disabled || loading !== null}
              startIcon={loading === option.type ? <CircularProgress size={16} /> : option.icon}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '0.8rem',
              }}
            >
              {option.label}
            </Button>
          </span>
        </Tooltip>
      ))}
      {error && (
        <Box sx={{ width: '100%', color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
          {error}
        </Box>
      )}
    </Box>
  );
};

export default QuickHelpButtons;
